import { useState } from "react";
import { useStore } from "./lib/store";
import { LANGS } from "./lib/types";
import Browse from "./ui/Browse";
import Home from "./ui/Home";
import Inbox from "./ui/Inbox";
import Ingest from "./ui/Ingest";
import Login from "./ui/Login";
import Review from "./ui/Review";
import Settings from "./ui/Settings";

type Tab = "home" | "review" | "add" | "capture" | "browse" | "settings";

const TABS: { id: Tab; label: string; glyph: string }[] = [
  { id: "home", label: "Today", glyph: "◎" },
  { id: "add", label: "Add", glyph: "＋" },
  { id: "capture", label: "Capture", glyph: "✎" },
  { id: "browse", label: "Cards", glyph: "☰" },
  { id: "settings", label: "Settings", glyph: "⚙" },
];

export default function App() {
  const store = useStore();
  const [tab, setTab] = useState<Tab>("home");
  const [filters, setFilters] = useState<{ sourceId: string | null; domain: string | null }>({
    sourceId: null,
    domain: null,
  });

  const pendingNotes = store.notes.filter((n) => n.lang === store.lang && n.status === "pending").length;

  if (store.locked) {
    return (
      <div className="app">
        <main>
          <Login onUnlocked={() => void store.unlock()} />
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="langswitch">
          {LANGS.map((lang) => (
            <button
              key={lang}
              data-lang={lang}
              aria-pressed={store.lang === lang}
              onClick={() => {
                store.setLang(lang);
                if (tab === "review") setTab("home");
              }}
            >
              {lang === "fr" ? "Français" : "Español"}
            </button>
          ))}
        </div>
        <div className="spacer" />
        <button className="syncstate" onClick={() => void store.syncNow()} title="Sync now">
          {store.syncing ? (
            <span className="spinner" />
          ) : (
            <span
              className={`dot${
                store.syncError ? " error" : !store.online ? " offline" : store.pending ? " pending" : ""
              }`}
            />
          )}
          {!store.online ? "Offline" : store.pending ? `${store.pending} queued` : "Synced"}
        </button>
      </header>

      <main className={tab === "review" ? "reviewing" : undefined}>
        {!store.ready ? (
          <div className="empty"><span className="spinner" /></div>
        ) : tab === "review" ? (
          <Review
            sourceId={filters.sourceId}
            domain={filters.domain}
            onExit={() => setTab("home")}
          />
        ) : tab === "home" ? (
          <Home
            onReview={(sourceId, domain) => {
              setFilters({ sourceId, domain });
              setTab("review");
            }}
            onGoToInbox={() => setTab("capture")}
          />
        ) : tab === "add" ? (
          <Ingest />
        ) : tab === "capture" ? (
          <Inbox />
        ) : tab === "browse" ? (
          <Browse />
        ) : (
          <Settings />
        )}
      </main>

      {tab !== "review" && (
        <nav className="tabs">
          <div>
            {TABS.map((entry) => (
              <button
                key={entry.id}
                aria-current={tab === entry.id ? "page" : undefined}
                onClick={() => setTab(entry.id)}
              >
                <span className="glyph">{entry.glyph}</span>
                {entry.label}
                {entry.id === "capture" && pendingNotes > 0 && (
                  <span className="badge">{pendingNotes}</span>
                )}
              </button>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
